/*
 * The register form's rules, out of the dialog so they can be tested.
 *
 * Shaped like stockMovements.js beside it: pure functions that take an `intl` and return the
 * `errors` map every CMMS dialog already renders through FieldError. The dialog keeps the state
 * and the boxes; what counts as a valid part lives here.
 *
 * These rules are a copy of the server's, not the authority. SparePartService.Validate is what
 * actually protects the table, and it runs whether a request came from this form or from
 * somewhere else entirely. The copy exists because the server's refusal arrives as a toast
 * carrying an English sentence about column names — true, and no use to someone looking at two
 * boxes marked Min and Max.
 */
import messages from "./messages";

/*
 * An empty field is not a zero.
 *
 * Number("") is 0, which would turn a blank unit cost into a part that costs nothing and a blank
 * level into a level of nought — and a reorder point of nought is a part nobody ever reorders,
 * which is not what leaving the box alone meant. The dialog uses this both to check the numbers
 * and to build the payload, so the line between blank and zero is drawn once.
 */
export const toNullableNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const isBlank = (value) => value === "" || value === null || value === undefined;

/*
 * Validates the add/edit form.
 *
 * Returns a field -> message map, empty when the form is fine.
 */
export const validateSparePartForm = (form, intl) => {
  const errors = {};

  if (!form?.partNumber?.trim()) {
    errors.partNumber = intl.formatMessage(messages.validationPartNumberRequired);
  }
  if (!form?.name?.trim()) {
    errors.name = intl.formatMessage(messages.validationNameRequired);
  }
  if (!form?.unit?.trim()) {
    errors.unit = intl.formatMessage(messages.validationUnitRequired);
  }

  // Blank is allowed on all three numbers; nonsense is not. Checked against the raw entry rather
  // than the parsed one, because toNullableNumber answers null for both "" and "abc" and only one
  // of those is someone leaving the box alone.
  if (!isBlank(form?.unitCost) && Number.isNaN(Number(form.unitCost))) {
    errors.unitCost = intl.formatMessage(messages.validationNumber);
  }
  if (!isBlank(form?.reorderPoint) && Number.isNaN(Number(form.reorderPoint))) {
    errors.reorderPoint = intl.formatMessage(messages.validationNumber);
  }
  if (!isBlank(form?.maxStockLevel) && Number.isNaN(Number(form.maxStockLevel))) {
    errors.maxStockLevel = intl.formatMessage(messages.validationNumber);
  }

  /*
   * Order at ten, order up to four is not a level pair, it is a typo.
   *
   * Only when both are set: either one alone is a legitimate half — a part can have a min and no
   * ceiling, or a ceiling and nothing that alarms. Equal is allowed and means what it says: order
   * at four, order up to four, keep exactly four on the shelf.
   *
   * Reported on max rather than on min. Both numbers are involved, but the one the person just
   * typed is the one they can move, and an error under the field they were not editing reads as
   * the form blaming the wrong box.
   *
   * Nothing unsaid can overwrite the not-a-number message above it: toNullableNumber answers null
   * for anything unparseable, so a pair with a typo in it never reaches the comparison.
   */
  const min = toNullableNumber(form?.reorderPoint);
  const max = toNullableNumber(form?.maxStockLevel);
  if (min !== null && max !== null && max < min) {
    errors.maxStockLevel = intl.formatMessage(messages.validationMaxBelowMin);
  }

  return errors;
};
